import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Users, ArrowRight } from "lucide-react";
import { Reveal, MaskedLine, Eyebrow, ZenLoader } from "../components/Reveal";

const CATEGORY_STYLE = {
  Meditazione: "bg-lavanda-mist text-lavanda",
  "Cucina Consapevole": "bg-earth-mist text-earth",
  Ritiro: "bg-sage-soft text-sage-deep",
  Yoga: "bg-sage-mist text-sage-deep",
  Percorsi: "bg-lavanda-soft text-lavanda",
};

const MONTHS = ["GEN", "FEB", "MAR", "APR", "MAG", "GIU", "LUG", "AGO", "SET", "OTT", "NOV", "DIC"];

const Eventi = () => {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events`)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(setEvents)
      .catch(() => setError(true));
  }, []);

  return (
    <div data-testid="eventi-page" className="pt-40">
      <header className="mx-auto max-w-6xl px-6">
        <Reveal y={16}><Eyebrow>Eventi &amp; Sessioni</Eyebrow></Reveal>
        <h1 className="mt-6 font-serif text-5xl leading-tight sm:text-6xl" data-testid="eventi-title">
          <MaskedLine delay={0.1} inView>Incontri che</MaskedLine>
          <MaskedLine delay={0.22} inView><span className="italic text-sage">nutrono l'anima</span></MaskedLine>
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-7 max-w-xl text-base leading-relaxed text-ink/65">
            Cerchi, laboratori e ritiri: momenti condivisi per praticare insieme,
            tra respiro, cucina consapevole e silenzio.
          </p>
        </Reveal>
      </header>

      <section className="mx-auto max-w-4xl px-6 pt-20">
        {!events && !error && <ZenLoader />}
        {error && (
          <p className="rounded-[2rem] bg-earth-mist p-8 text-center text-sm text-earth" data-testid="eventi-error">
            Non riesco a caricare gli eventi in questo momento. Scrivimi su WhatsApp per il calendario aggiornato.
          </p>
        )}
        {events && events.length === 0 && (
          <p className="rounded-[2rem] bg-cream-soft p-8 text-center text-sm text-ink/60" data-testid="eventi-empty">
            Nuovi eventi in arrivo. Scrivimi per essere avvisata/o.
          </p>
        )}
        <div className="space-y-6">
          {events?.map((e, i) => {
            const d = new Date(`${e.date}T00:00:00`);
            return (
              <Reveal key={e.id} delay={0.05 * i}>
                <article
                  data-testid={`event-card-${e.id}`}
                  className="group flex flex-col gap-6 rounded-[2.5rem] bg-cream-soft p-7 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_56px_rgba(48,51,48,0.1)] sm:flex-row sm:items-center sm:gap-10 sm:p-9"
                >
                  <div className="flex shrink-0 items-baseline gap-3 sm:flex-col sm:items-center sm:gap-0 sm:text-center">
                    <span className="font-serif text-5xl italic text-sage sm:text-6xl">{d.getDate()}</span>
                    <span className="text-xs font-semibold uppercase tracking-[0.3em] text-ink/40">
                      {MONTHS[d.getMonth()]} {d.getFullYear()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <span className={`rounded-full px-4 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.2em] ${CATEGORY_STYLE[e.category] || "bg-muted text-ink/60"}`}>
                      {e.category}
                    </span>
                    <h2 className="mt-4 font-serif text-2xl">{e.title}</h2>
                    <p className="mt-2.5 text-sm leading-relaxed text-ink/60">{e.description}</p>
                    <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-xs text-ink/50">
                      <span className="flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> {e.time}</span>
                      <span className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {e.location}</span>
                      <span className="flex items-center gap-2"><Users className="h-3.5 w-3.5" /> {e.spots} posti</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center justify-between gap-6 sm:flex-col sm:items-end">
                    <span className="font-serif text-2xl italic text-sage-deep">{e.price}</span>
                    <Link
                      to={`/contatti?servizio=${encodeURIComponent(`Evento: ${e.title}`)}`}
                      data-testid={`event-book-${e.id}`}
                      className="flex items-center gap-2 rounded-full bg-[linear-gradient(135deg,#4F645B,#7A9488)] px-6 py-3 text-xs font-semibold text-cream transition-transform duration-500 hover:scale-105"
                    >
                      Riserva <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Eventi;
